import type { NavEntry } from '@/features/auth/types'

/**
 * Display-only layout for the sidebar: which section heading each nav entry
 * sits under, in what order, and its sidebar label. Keyed by
 * navigation_registry.py's nav keys. This never decides visibility - the
 * session's permission-filtered `nav` is still the only source of what a
 * user sees; an entry simply isn't here to group if they can't see it, and
 * a section with no visible entries renders no heading.
 */
interface Section {
  /** Heading text; null = the untitled top block. */
  title: string | null
  keys: string[]
}

const SECTIONS: Section[] = [
  { title: null, keys: ['dashboard', 'controller_status'] },
  { title: 'Assets', keys: ['clients', 'controller_directory', 'microgrids', 'controllers', 'consolidation'] },
  { title: 'Monitoring', keys: ['alarm_subscriptions', 'maintenance'] },
  { title: 'Reports', keys: ['report_groups', 'monthly_reports'] },
  { title: 'Administration', keys: ['user_access', 'users', 'privileges', 'permissions', 'feature_flags'] },
]

/** Heading for any entry not listed above (e.g. one newly added to the registry). */
const FALLBACK_SECTION = 'More'

const LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  controller_status: 'Controller status',
  clients: 'Companies',
  controller_directory: 'Controllers',
  microgrids: 'Microgrids',
  controllers: 'IoT management',
  consolidation: 'Consolidation',
  alarm_subscriptions: 'Alarms config',
  alarm_topics: 'Alarm topics',
  fleet_alarm_subscriptions: 'Fleet subscriptions',
  fleet_alarm_subscriptions_manage: 'Manage subscriptions',
  maintenance: 'Maintenance notes',
  report_groups: 'Report configuration',
  monthly_reports: 'Monthly reports',
  user_access: 'User access',
  users: 'Users',
  privileges: 'User privileges',
  permissions: 'User permissions',
  feature_flags: 'Feature flags',
}

/** Sidebar label for a nav key, falling back to the registry's own label. */
export function navLabel(key: string, fallback: string): string {
  return LABELS[key] ?? fallback
}

export interface NavSection {
  title: string | null
  entries: NavEntry[]
}

/**
 * Buckets the session's nav into sections. Within a known section entries
 * follow SECTIONS' order; unknown entries keep the registry's own order
 * under FALLBACK_SECTION at the end. Group children are left untouched.
 */
export function sectionNav(nav: NavEntry[]): NavSection[] {
  const placed = new Set<string>()
  const out: NavSection[] = []
  for (const section of SECTIONS) {
    const entries = section.keys
      .map((key) => nav.find((e) => e.key === key))
      .filter((e): e is NavEntry => e !== undefined)
    entries.forEach((e) => placed.add(e.key))
    if (entries.length) out.push({ title: section.title, entries })
  }
  const rest = nav.filter((e) => !placed.has(e.key))
  if (rest.length) out.push({ title: FALLBACK_SECTION, entries: rest })
  return out
}

